import { useState } from 'react';
import { discardPending, isStuck } from '../offline/queue.js';
import { useOfflineQueue } from '../offline/useOfflineQueue.js';

const KIND_LABELS: Record<string, string> = {
  diary: 'Eintrag',
  water: 'Wasser',
  weight: 'Gewicht',
  activity: 'Aktivität',
};

/**
 * The only place the queue is visible.
 *
 * It has to be, and it has to be honest: turning the queue on changes what
 * "gespeichert" means, and an app that silently holds three meals in a browser
 * database has quietly become the only copy of them. Showing the count is what
 * keeps that a decision rather than a surprise.
 *
 * Renders nothing when the feature is off or there is nothing waiting — the
 * common case must cost the user no screen space.
 */
export function OfflineBanner(): JSX.Element | null {
  const { enabled, online, pending, flush } = useOfflineQueue();
  const [expanded, setExpanded] = useState(false);

  if (!enabled) return null;
  if (online && pending.length === 0) return null;

  const stuck = pending.filter(isStuck);

  return (
    <div className={`offline-banner ${online ? '' : 'offline-banner-offline'}`.trim()}>
      <button
        type="button"
        className="offline-banner-summary"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
      >
        <span className="offline-banner-dot" aria-hidden="true" />
        <span>
          {!online && pending.length === 0 && 'Offline — Einträge werden gespeichert'}
          {!online && pending.length > 0 && `Offline — ${pending.length} warten`}
          {online && pending.length > 0 && `${pending.length} werden übertragen`}
        </span>
        {pending.length > 0 && (
          <span className="offline-banner-chevron">{expanded ? '▾' : '▸'}</span>
        )}
      </button>

      {expanded && pending.length > 0 && (
        <ul className="offline-banner-list">
          {pending.map((write) => (
            <li key={write.id}>
              <div>
                <strong>{KIND_LABELS[write.kind] ?? write.kind}</strong> {write.label}
                {isStuck(write) && write.lastError && (
                  <div className="offline-banner-error">{write.lastError}</div>
                )}
              </div>
              {isStuck(write) && (
                <button type="button" onClick={() => void discardPending(write.id)}>
                  Verwerfen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {online && pending.length > 0 && (
        <button type="button" className="offline-banner-action" onClick={flush}>
          Jetzt senden
        </button>
      )}

      {stuck.length > 0 && (
        <p className="offline-banner-error">
          {stuck.length === 1 ? 'Ein Eintrag lässt' : `${stuck.length} Einträge lassen`} sich nicht
          übertragen. Der Server hat sie abgelehnt — verwerfen und neu eintragen.
        </p>
      )}
    </div>
  );
}
